import { createPortal } from 'react-dom';
import { useToasts, useToastDismiss, type ToastItem } from '@/lib/toast';
import { CheckCircle2, AlertCircle, Info, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const icons = {
    success: CheckCircle2,
    error: AlertCircle,
    info: Info,
    loading: Loader2
};

const styles = {
    success: 'bg-green-500/10 border-green-500/40 text-green-300',
    error: 'bg-red-500/10 border-red-500/40 text-red-300',
    info: 'bg-blue-500/10 border-blue-500/40 text-blue-300',
    loading: 'bg-zinc-800 border-zinc-700 text-zinc-200'
};

function ToastCard({ item }: { item: ToastItem }) {
    const dismiss = useToastDismiss();
    const Icon = icons[item.kind];
    return (
        <div
            className={cn(
                'flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg backdrop-blur-md animate-in slide-in-from-bottom',
                styles[item.kind]
            )}
            role="status"
        >
            <Icon className={cn('w-5 h-5 shrink-0 mt-0.5', item.kind === 'loading' && 'animate-spin')} />
            <div className="flex-1 text-sm font-medium leading-snug">{item.message}</div>
            <button
                onClick={() => dismiss(item.id)}
                aria-label="Chiudi"
                className="shrink-0 text-zinc-500 hover:text-zinc-300 p-1 -m-1 rounded"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}

export function ToastViewport() {
    const items = useToasts();
    if (!items.length) return null;
    return createPortal(
        <div
            className="fixed bottom-20 left-0 right-0 z-[70] flex flex-col gap-2 px-4 pointer-events-none"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
            <div className="mx-auto w-full max-w-[380px] flex flex-col gap-2 pointer-events-auto">
                {items.map(item => (
                    <ToastCard key={item.id} item={item} />
                ))}
            </div>
        </div>,
        document.body
    );
}
